/**
 * User-facing copy when portal and Core policy lists do not intersect (extend finder).
 */

function normalizePolicyNo(p) {
  return String(p?.policyNo || p?.policy_number || '').trim().toUpperCase();
}

/**
 * @param {Array<{ policyNo: string, certificateStatus?: string|null, certificateLastError?: string|null, dmvicCertificateNumber?: string|null, coreSubmissionStatus?: string|null }>} portalPolicies
 * @param {Array<{ policyNo?: string, policy_number?: string }>} corePolicies
 */
export function buildPortalCoreMismatchMessage(portalPolicies, corePolicies) {
  const portal = Array.isArray(portalPolicies) ? portalPolicies : [];
  if (portal.length === 0) {
    return 'No Core policy for this vehicle matches a cover issued through Agent New Portal. Only portal-issued covers can be extended here.';
  }

  const coreNos = [
    ...new Set(
      (Array.isArray(corePolicies) ? corePolicies : [])
        .map(normalizePolicyNo)
        .filter(Boolean)
    )
  ];

  const detail = portal
    .map((p) => {
      const pol = String(p.policyNo || '').trim();
      const cert = String(p.certificateStatus || '').toLowerCase();
      if (cert === 'failed') {
        const err = String(p.certificateLastError || '').trim();
        const short = err ? ` ${err.slice(0, 160)}${err.length > 160 ? '…' : ''}` : '';
        return `${pol} (payment on portal; certificate not issued${short})`;
      }
      const coreSubmitted =
        String(p.coreSubmissionStatus || '').toLowerCase() === 'submitted';
      if (cert === 'issued' && p.dmvicCertificateNumber && coreSubmitted) {
        return `${pol} (certificate ${p.dmvicCertificateNumber}; in Core but not on vehicle risk list — ask IT/Underwriting to link risk, or retry after portal update)`;
      }
      if (cert === 'issued' && p.dmvicCertificateNumber) {
        return `${pol} (certificate ${p.dmvicCertificateNumber}; not sent to Core yet)`;
      }
      if (cert === 'issued') {
        return `${pol} (issued in portal; not listed on Core risk search)`;
      }
      return `${pol} (portal record; cover not fully issued)`;
    })
    .join('; ');

  const coreHint =
    coreNos.length > 0
      ? ` Core has other policy number(s) on this registration: ${coreNos.join(', ')}.`
      : '';

  return (
    `This registration has a portal purchase (${detail}), but that policy number is not in Core, so Buy/Extend cannot continue here.${coreHint} ` +
    'Resolve the failed certificate or ask Underwriting to align Core with the portal policy before extending.'
  );
}

export const CORE_TIMEOUT_ERROR =
  'Core did not respond in time. Buy/Extend needs a live Core policy match for this registration.';

export const CORE_UNAVAILABLE_ERROR =
  'Core could not load policies for this registration. The Core risk lookup is required and did not return policies.';

/**
 * Finder copy when Core /policies/risk fails. Empty string means Core succeeded.
 * @param {number} status
 * @param {{ message?: string, error?: string, timedOut?: boolean }|null} payload
 */
export function coreRiskLookupError(status, payload) {
  if (status >= 200 && status < 300) return '';
  const msg = String(payload?.message || payload?.error || '');
  if (payload?.timedOut || /timeout/i.test(msg) || /timed\s*out/i.test(msg)) {
    return CORE_TIMEOUT_ERROR;
  }
  if (status >= 500 || /internal server error/i.test(msg)) {
    return CORE_UNAVAILABLE_ERROR;
  }
  return msg || CORE_UNAVAILABLE_ERROR;
}
